import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Spinner, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import { getAdvancesByEmployee } from "features/EmployeeAdvances/services/EmployeeAdvanceService";
import type { EmployeeAdvance } from "features/EmployeeAdvances/types/employee-advance.types";
import { employeeAdvanceStatusMap } from "features/EmployeeAdvances/constants/employee-advance.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useEmployeeOverview } from "./EmployeeOverviewContext";

const EmployeeAdvancesTab = () => {
  const { can } = useAccess();
  const canReadAdvance = can(Permissions.EMPLOYEE_ADVANCE.READ);
  const canCreateAdvance = can(Permissions.EMPLOYEE_ADVANCE.CREATE);
  const { employee } = useEmployeeOverview();
  const [advances, setAdvances] = useState<EmployeeAdvance[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employee?.id) return;
    let cancelled = false;
    setLoading(true);
    getAdvancesByEmployee(employee.id)
      .then((res) => {
        if (!cancelled && res.success && res.data?.rows) {
          setAdvances(res.data.rows as unknown as EmployeeAdvance[]);
        }
      })
      .catch(console.warn)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employee?.id]);

  const hasActiveAdvance = advances.some((a) => a.status === "PROCESSED");

  return (
    <Row>
      <Col lg={12}>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-uppercase text-muted ">Advances</h6>
              {canCreateAdvance && !hasActiveAdvance ? (
                <Link
                  to={`/finance/employee-advances/create?employeeId=${employee?.id}`}
                  className="btn btn-primary btn-sm"
                >
                  <i className="ri-add-line align-bottom me-1" />
                  New advance
                </Link>
              ) : null}
            </div>

            {loading ? (
              <div className="text-center py-4">
                <Spinner size="sm" className="me-2" />
                <span className="text-muted">Loading advances…</span>
              </div>
            ) : advances.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="ri-hand-coin-line fs-24 d-block mb-2 opacity-50" />
                No advances recorded for this employee.
              </div>
            ) : (
              <div className="table-responsive table-card">
                <Table className="table-borderless table-hover align-middle mb-0">
                  <thead className="table-light text-muted">
                    <tr>
                      <th>Code</th>
                      <th>Amount</th>
                      <th>Settled</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advances.map((a) => (
                      <tr key={a.id}>
                        <td className="fw-medium">
                          {canReadAdvance ? (
                            <Link to={`/finance/employee-advances/${a.id}`}>
                              {a.code}
                            </Link>
                          ) : (
                            a.code
                          )}
                        </td>
                        <td>{formatCurrency(a.amount)}</td>
                        <td>{formatCurrency(a.settledAmount ?? 0)}</td>
                        <td>
                          <StatusBadge
                            value={a.status}
                            map={employeeAdvanceStatusMap}
                          />
                        </td>
                        <td className="text-muted small">
                          <DateTime date={a.created_at} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default EmployeeAdvancesTab;
